import React, { useEffect, useMemo } from "react";
import {
    Button,
    Grid,
    InputLabel,
    OutlinedInput,
    Paper,
    SpeedDial,
    Select,
    MenuItem,
    FormControl
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import { usePaginasCustomizadasController } from "./context/paginas-customizadas-controller";
import { usePaginasCustomizadasState, usePaginasCustomizadasDispatch } from "./context/paginas-customizadas-controller";
import { ACTION_TYPES } from "./context/paginas-customizadas-reducer";
import { PaginaCustomizadaDrawer } from "./components/pagina-customizada-drawer";
import { getDataGridLocaleText, dataGridDefaultSx, dataGridDefaultProps } from "../../../config/datagrid-config";

export const PaginasCustomizadas = () => {
    const {
        dataPaginatedListResponse,
        isLoadingData,
        dataSearchRequest
    } = usePaginasCustomizadasState();

    const paginasCustomizadasDispatch = usePaginasCustomizadasDispatch();

    const {
        loadData,
        onChangePagination,
        getColumnsGrid,
        openNewForm
    } = usePaginasCustomizadasController();

    useEffect(() => {
        (async () => {
            await loadData(dataSearchRequest);
        })();
    }, []);

    const paginationModel = useMemo(() => ({
        page: dataSearchRequest?.page ?? 0,
        pageSize: dataPaginatedListResponse?.pageSize ?? 50
    }), [dataSearchRequest?.page, dataPaginatedListResponse?.pageSize]);

    const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataSearchRequest,
            payload: {
                ...dataSearchRequest,
                term: event.target.value
            }
        });
    };

    const handlePublishedFilterChange = (event: any) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataSearchRequest,
            payload: {
                ...dataSearchRequest,
                published: event.target.value === '' ? null : event.target.value === 'true'
            }
        });
    };

    const handleSearch = async () => {
        await loadData(dataSearchRequest);
    };

    const handleClear = async () => {
        const clearedRequest = {
            page: 1,
            pageSize: 10,
            term: '',
            published: null
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataSearchRequest,
            payload: clearedRequest
        });

        await loadData(clearedRequest);
    };

    return (
        <React.Fragment>
            <PaginaCustomizadaDrawer />

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Grid container spacing={1.5}>
                        {/* Search Term */}
                        <Grid item lg={5} sm={6} xl={5}>
                            <InputLabel>Buscar por título</InputLabel>
                            <OutlinedInput
                                name="input-term"
                                size="small"
                                value={dataSearchRequest?.term || ''}
                                onChange={handleSearchTermChange}
                                fullWidth
                                placeholder="Digite o título..."
                            />
                        </Grid>

                        {/* Published Filter */}
                        <Grid item lg={3} sm={4} xl={3}>
                            <InputLabel>Status</InputLabel>

                            <Select
                                size="small"
                                fullWidth
                                value={dataSearchRequest?.published === null ? '' : String(dataSearchRequest?.published)}
                                onChange={handlePublishedFilterChange}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Publicado</MenuItem>
                                <MenuItem value="false">Rascunho</MenuItem>
                            </Select>
                        </Grid>

                        {/* Search Button */}
                        <Grid
                            item
                            lg={2}
                            sm={2}
                            xl={2}
                            sx={{
                                mt: 1,
                                display: 'flex',
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                alignItems: "flex-end"
                            }}
                        >
                            <Button
                                variant="contained"
                                fullWidth
                                size="small"
                                onClick={handleSearch}
                                startIcon={<SearchIcon />}
                            >
                                Consultar
                            </Button>
                        </Grid>

                        {/* Clear Button */}
                        <Grid
                            item
                            lg={2}
                            sm={2}
                            xl={2}
                            sx={{
                                mt: 1,
                                display: 'flex',
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                alignItems: "flex-end"
                            }}
                        >
                            <Button
                                variant="outlined"
                                fullWidth
                                size="small"
                                onClick={handleClear}
                                startIcon={<ClearIcon />}
                            >
                                Limpar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        minHeight: "500px",
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <DataGrid
                        rows={dataPaginatedListResponse.items}
                        columns={getColumnsGrid()}
                        {...dataGridDefaultProps}
                        onPaginationModelChange={(e) => {
                            onChangePagination(e.page, e.pageSize);
                        }}
                        sx={dataGridDefaultSx}
                        localeText={getDataGridLocaleText(
                            dataPaginatedListResponse.totalRecords,
                            dataPaginatedListResponse.pageSize
                        )}
                        paginationModel={paginationModel}
                        rowCount={dataPaginatedListResponse.totalRecords}
                        loading={isLoadingData}
                    />
                    <SpeedDial
                        ariaLabel="SpeedDial basic example"
                        sx={{ position: 'absolute', bottom: 16, right: 16 }}
                        onClick={openNewForm}
                        icon={<SpeedDialIcon />}
                    />
                </Paper>
            </Grid>
        </React.Fragment>
    );
};
